import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import alqemiDataService, {
  Candidate,
  Offer,
} from "../../api/AlqemiDataService";

interface AlqemiFilter {
  field: string;
  value: any;
  operator?: string;
}

interface AlqemiDashboardContextType {
  offers: Offer[];
  candidates: Candidate[];
  currentFilters: AlqemiFilter[];
  loadingData: boolean;
  setCandidates: (candidates: Candidate[]) => void;
  addAlqemiFilter: (filter: AlqemiFilter) => void;
  removeAlqemiFilter: (field: string) => void;
  replaceAlqemiFilters: (filters: AlqemiFilter[]) => void;
  refreshData: () => void;
}

const AlqemiDashboardContext = createContext<AlqemiDashboardContextType | null>(
  null
);

export const useAlqemiDashboard = () => {
  const context = useContext(AlqemiDashboardContext);
  if (!context) {
    throw new Error(
      "useAlqemiDashboard must be used within AlqemiDashboardProvider"
    );
  }
  return context;
};

interface AlqemiDashboardProviderProps {
  children: React.ReactNode;
}

export default function AlqemiDashboardProvider({
  children,
}: AlqemiDashboardProviderProps) {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [currentFilters, setCurrentFilters] = useState<AlqemiFilter[]>(() => {
    const savedFilters = localStorage.getItem("alqemiFilters");
    if (!savedFilters) return [];
    // Exclude searchQuery from restored filters — the search input resets on refresh
    return JSON.parse(savedFilters).filter((f: AlqemiFilter) => f.field !== "searchQuery");
  });
  const [loadingData, setLoadingData] = useState(true);

  const fetchData = useCallback(async () => {
    setLoadingData(true);
    try {
      // Build query params from filters
      const params: any = {};
      // Default behavior: show only top offers unless explicitly set to show all
      const showOnlyTopOffersFilter = currentFilters.find(f => f.field === "showOnlyTopOffers");
      params.showOnlyTopOffers = showOnlyTopOffersFilter === undefined ? true : showOnlyTopOffersFilter.value;
      
      currentFilters.forEach((filter) => {
        if (filter.field === "currentCandidate") {
          params.currentCandidate = filter.value;
        } else if (filter.field === "searchQuery") {
          params.searchQuery = filter.value;
        }
      });

      // Fetch data in parallel
      const [offersData, candidatesData] = await Promise.all([
        alqemiDataService.getOffers(params),
        alqemiDataService.getCandidates(),
      ]);

      // Filter out offers with scraping_failed = true
      const filteredOffers = offersData.filter(
        (offer) => !offer.scraping_failed
      );

      setOffers(filteredOffers);
      setCandidates(candidatesData);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoadingData(false);
    }
  }, [currentFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("alqemiFilters", JSON.stringify(currentFilters));
  }, [currentFilters]);

  const addAlqemiFilter = (filter: AlqemiFilter) => {
    setCurrentFilters((prev) => {
      const existing = prev.findIndex((f) => f.field === filter.field);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = filter;
        return updated;
      }
      return [...prev, filter];
    });
  };

  const removeAlqemiFilter = (field: string) => {
    setCurrentFilters((prev) => prev.filter((f) => f.field !== field));
  };

  const replaceAlqemiFilters = (filters: AlqemiFilter[]) => {
    setCurrentFilters(filters);
  };

  const refreshData = () => {
    fetchData();
  };

  const value: AlqemiDashboardContextType = {
    offers,
    candidates,
    currentFilters,
    loadingData,
    setCandidates,
    addAlqemiFilter,
    removeAlqemiFilter,
    replaceAlqemiFilters,
    refreshData,
  };

  return (
    <AlqemiDashboardContext.Provider value={value}>
      {children}
    </AlqemiDashboardContext.Provider>
  );
}